/** Help articles: Markdown with a small frontmatter block (see content/help/README.md). */

export const HELP_CATEGORIES = ['start', 'agenda', 'klanten', 'online', 'team', 'account'] as const;
export const HELP_SCREENS = ['vandaag', 'agenda', 'klanten', 'salon', 'welkom', 'account', 'diensten', 'team', 'openingstijden', 'bedrijf', 'online-boeken', 'berichten', 'locaties', 'privacy'] as const;
export type HelpCategory = (typeof HELP_CATEGORIES)[number];
export type HelpScreen = (typeof HELP_SCREENS)[number];
export type HelpLocale = 'nl' | 'en';

export type HelpArticle = {
  slug: string;
  locale: HelpLocale;
  title: string;
  summary: string;
  category: HelpCategory;
  order: number;
  screens: HelpScreen[];
  /** Markdown without the frontmatter. */
  body: string;
};

/** Reads one article; throws with the file name in the message when something is missing. */
export function parseArticle(raw: string, slug: string, locale: HelpLocale): HelpArticle {
  const m = /^---\n([\s\S]*?)\n---\n([\s\S]*)$/.exec(raw.replace(/\r\n/g, '\n'));
  if (!m) throw new Error(`${locale}/${slug}: missing frontmatter`);
  const fields = new Map<string, string>();
  for (const line of m[1]!.split('\n')) {
    const i = line.indexOf(':');
    if (i > 0) fields.set(line.slice(0, i).trim(), line.slice(i + 1).trim());
  }
  const need = (k: string) => {
    const v = fields.get(k);
    if (!v) throw new Error(`${locale}/${slug}: missing "${k}"`);
    return v;
  };
  const category = need('category') as HelpCategory;
  if (!HELP_CATEGORIES.includes(category)) throw new Error(`${locale}/${slug}: unknown category "${category}"`);
  const screens = need('screens').replace(/^\[|\]$/g, '').split(',').map((s) => s.trim()).filter(Boolean) as HelpScreen[];
  for (const s of screens) if (!HELP_SCREENS.includes(s)) throw new Error(`${locale}/${slug}: unknown screen "${s}"`);
  const order = Number(need('order'));
  if (!Number.isFinite(order)) throw new Error(`${locale}/${slug}: order must be a number`);
  return { slug, locale, title: need('title'), summary: need('summary'), category, order, screens, body: m[2]!.trim() };
}

/** Where in the app we are, for "help for this screen". */
export function screenFromPath(pathname: string): HelpScreen | null {
  const parts = pathname.split('/').filter(Boolean);
  if (parts[0] !== 's' || parts.length < 3) return null;
  const key = parts[2] === 'salon' && parts[3] ? parts[3] : parts[2]!;
  return (HELP_SCREENS as readonly string[]).includes(key) ? (key as HelpScreen) : null;
}

const fold = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

/** Every word must appear; titles count most. Good enough for 15 articles, no index needed. */
export function searchArticles(articles: HelpArticle[], query: string): HelpArticle[] {
  const words = fold(query).split(/\s+/).filter((w) => w.length > 1);
  if (!words.length) return [];
  return articles
    .map((a) => {
      const title = fold(a.title);
      const summary = fold(a.summary);
      const body = fold(a.body);
      let score = 0;
      for (const w of words) {
        const s = (title.includes(w) ? 6 : 0) + (summary.includes(w) ? 3 : 0) + (body.includes(w) ? 1 : 0);
        if (!s) return null;
        score += s;
      }
      return { a, score };
    })
    .filter((x): x is { a: HelpArticle; score: number } => !!x)
    .sort((x, y) => y.score - x.score)
    .map((x) => x.a);
}

export const byOrder = (a: HelpArticle, b: HelpArticle) => HELP_CATEGORIES.indexOf(a.category) - HELP_CATEGORIES.indexOf(b.category) || a.order - b.order;
